import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";
import MaintenancePage from "./pages/MaintenancePage";
import { getAppVersion, clearAuthStorage, getMaintenanceStatus } from "./services/api";
import LoginPage from "./pages/LoginPage";
import Dashboard from "../src/pages/Dashboard";
import Listing from "../src/pages/Listing";
import Calendar from "../src/pages/Calendar";
import CalendarSingle from "../src/pages/CalendarSingle";
import Review from "../src/pages/Review";
// import Documents from "../src/pages/Documents";
import Setting from "../src/pages/Setting";
import Chat from '../src/pages/Chat';
import UserListing from '../src/pages/UserListing';
import UserSetting from "./pages/UserSetting";
import AdminAttention from "../src/pages/AdminAttention";
import PropertyListing from "./pages/PropertyListing";
import AdminPropertyPage from "../src/pages/AdminPropertyPage";
import RolesPermissions from "../src/pages/RolesPermissions";
import SuperAdminLoginPage from "./pages/SuperAdminLoginPage";
import AdminMaintenance from "./pages/AdminMaintenance";
import UserActivityLogs from "./pages/UserActivityLogs";
import 'typeface-roboto';
import { GlobalStyle } from '../src/style-comps/GlobalStyles';
import DashboardHome from "../src/pages/DashboardHome";
import UserLogin from "./pages/UserLogin";
import { AdminUsersProvider } from './context/AdminUsersContext';
import { RoutePermissionWrapper } from './components/RoutePermissionWrapper';
import { SmartPermissionRoute } from './components/RoutePermissionWrapper';
import useVersionCheck from './hooks/useVersionCheck';


// 依據建置環境控制 console：production build 一律關閉，不依賴網域判斷
const isConsoleAllowed = process.env.NODE_ENV !== 'production';

if (!isConsoleAllowed) {
  console.log = () => { };
  console.warn = () => { };
  console.error = () => { };
}

// 路由變化時重新檢查維護模式
const RouteChangeChecker = ({ setIsMaintenance, setMaintenanceInfo }) => {
  const location = useLocation();
  useEffect(() => {

    // ✅ 每次切換頁面也檢查版本
    const checkVersion = async () => {
      const latestVersion = await getAppVersion();
      if (!latestVersion) return;
      const storedVersion = localStorage.getItem('app_version');
      if (storedVersion && storedVersion !== latestVersion) {
        clearAuthStorage();
        localStorage.setItem('app_version', latestVersion);
        window.location.reload(true);
      }
    };
    checkVersion();

    const checkMaintenance = async () => {
      try {
        const data = await getMaintenanceStatus();
        if (data?.IsEnabled === true) {
          setIsMaintenance(true);
          setMaintenanceInfo(data);
        } else {
          setIsMaintenance(false);
          setMaintenanceInfo(null);
        }
      } catch (err) {
        console.error('維護狀態檢查失敗:', err);
      }
    };
    checkMaintenance();
  }, [location.pathname]);
  return null;
};

function App() {
  const { isVersionChecked } = useVersionCheck();
  const [isLoggedIn, setIsLoggedIn] = useState(() => {
    const token = localStorage.getItem('authToken');
    const expiration = localStorage.getItem('tokenExpiration');
    if (token && expiration && new Date(expiration) > new Date()) {
      return true;
    }
    return false;
  });
  const [userRole, setUserRole] = useState(() => {
    const roles = JSON.parse(localStorage.getItem('roles')) || [];
    const isSuperAdmin = roles.some(r => r.toLowerCase() === 'superadmin');
    const isAdmin = roles.some(r => r.toLowerCase() === 'admin');
    return isSuperAdmin ? 'SuperAdmin' : isAdmin ? 'Admin' : roles[0] || '';
  });
  const [isMaintenance, setIsMaintenance] = useState(false);
  const [maintenanceInfo, setMaintenanceInfo] = useState(null);
  const [isCheckingMaintenance, setIsCheckingMaintenance] = useState(true);
  const isMaintenanceRef = React.useRef(false);

  useEffect(() => {
    const checkMaintenance = async () => {
      try {
        const data = await getMaintenanceStatus();
        if (data?.IsEnabled === true) {
          isMaintenanceRef.current = true;
          setIsMaintenance(true);
          setMaintenanceInfo(data);
        } else {
          if (isMaintenanceRef.current) {
            // 維護模式剛關閉，清除登入狀態強制重新登入
            const currentRoles = JSON.parse(localStorage.getItem('roles') || '[]');
            const currentIsSuperAdmin = currentRoles.some(r => r.toLowerCase() === 'superadmin');
            if (!currentIsSuperAdmin) {
              isMaintenanceRef.current = false;
              localStorage.removeItem('authToken');
              localStorage.removeItem('tokenExpiration');
              localStorage.removeItem('roles');
              localStorage.removeItem('authData');
              setIsLoggedIn(false);
              window.location.href = '/';
              return;
            }
          }
          isMaintenanceRef.current = false;
          setIsMaintenance(false);
          setMaintenanceInfo(null);
        }
      } catch (err) {
        console.error('維護狀態檢查失敗:', err);
      } finally {
        setIsCheckingMaintenance(false);
      }
    };

    checkMaintenance();

    // 每 30 秒重新檢查一次
    const interval = setInterval(checkMaintenance, 10000);
    return () => clearInterval(interval);
  }, []);



  const handleLogin = () => {
    const roles = JSON.parse(localStorage.getItem('roles')) || [];
    const isSuperAdmin = roles.some(r => r.toLowerCase() === 'superadmin' || r.toLowerCase() === 'super_admin');
    const isAdmin = roles.some(r => r.toLowerCase() === 'admin');
    const role = isSuperAdmin ? 'SuperAdmin' : isAdmin ? 'Admin' : roles[0] || '';
    setUserRole(role);
    setIsLoggedIn(true);
  };




  const roles = JSON.parse(localStorage.getItem('roles')) || [];
  const isSuperAdminUser = roles.some(r => r.toLowerCase() === 'superadmin');
  // User Listing / Property Listing（含其編輯頁）現在 SuperAdmin 也能管理，不再只限 Admin。
  const canManageListings = userRole === 'Admin' || userRole === 'SuperAdmin';
  if (!isVersionChecked) return null;
  if (isCheckingMaintenance) {
    return null;
  }

  const isOnSuperAdminPage = window.location.pathname === '/superadmin';

  if (isMaintenance && !isSuperAdminUser && !isOnSuperAdminPage) {
    return (
      <>
        <GlobalStyle />
        <MaintenancePage
          message={maintenanceInfo?.Message}
          recoveryTime={maintenanceInfo?.RecoveryTimeUtc}
          startTime={maintenanceInfo?.UpdatedAtUtc}
        />
      </>
    );
  }

  return (
    <>
      <GlobalStyle />
      <Router>
        <AdminUsersProvider>
          <RouteChangeChecker setIsMaintenance={setIsMaintenance} setMaintenanceInfo={setMaintenanceInfo} />
          <Routes>
            {/* 登入頁面 */}

            <Route path="/" element={isLoggedIn ? <Navigate to="/dashboard" replace /> : <LoginPage onLogin={handleLogin} />} />
            <Route path="/superadmin" element={<SuperAdminLoginPage onLogin={handleLogin} />} />
            <Route path="/login" element={isLoggedIn ? <Navigate to="/dashboard" replace /> : <LoginPage onLogin={handleLogin} />} />
            <Route path="*" element={<LoginPage />} />

            {/* Dashboard 框架 */}
            <Route
              path="/dashboard"
              element={isLoggedIn ? <Dashboard userRole={userRole} setIsLoggedIn={setIsLoggedIn} /> : <Navigate to="/" replace />}
            >
              <Route index element={
                localStorage.getItem('requirePasswordChange') === 'true'
                  ? <Navigate to="/dashboard/setting" replace />
                  : userRole === 'Admin' ? <Navigate to="/dashboard/userlisting" replace />
                    : userRole === 'SuperAdmin' ? <Navigate to="/dashboard/admin-attention" replace />
                      : <DashboardHome />
              } />
              <Route path="userlisting" element={canManageListings ? <UserListing /> : <Navigate to="/dashboard" />} />
              <Route
                path="admin-attention"
                element={
                  <SmartPermissionRoute permissions={['System.FullAccess']} legacyRole="SuperAdmin" userRole={userRole}>
                    <AdminAttention />
                  </SmartPermissionRoute>
                }
              />
              <Route
                path="admin-maintenance"
                element={userRole === 'SuperAdmin' ? <AdminMaintenance /> : <Navigate to="/dashboard" replace />}
              />
              <Route path="user-setting/:id" element={canManageListings ? <UserSetting /> : <Navigate to="/dashboard" />} />
              <Route
                path="property-listing"
                element={canManageListings ? <PropertyListing /> : <Navigate to="/dashboard" />}
              />
              <Route
                path="roles-permissions"
                element={userRole === 'Admin' || userRole === 'SuperAdmin' ? <RolesPermissions /> : <Navigate to="/dashboard" replace />}
              />
              <Route
                path="user-activity-logs"
                element={userRole === 'Admin' ? <UserActivityLogs /> : <Navigate to="/dashboard" replace />}
              />
              <Route
                path="property/:listingId"
                element={canManageListings ? <AdminPropertyPage /> : <Navigate to="/dashboard" />}
              />


              {/* 其他子路由 */}
              <Route path="listing" element={<Listing />} />
              <Route path="calendar" element={<Calendar />} />
              <Route path="calendar/:listingId" element={<CalendarSingle />} />
              <Route path="review" element={<Review />} />
              {/* <Route path="documents" element={<Documents />} /> */}
              <Route path="setting" element={<Setting />} />
              <Route path="chat" element={<Chat />} />
            </Route>

            {/* 其他子頁面... */}
            {/* 只有 Admin 才能訪問 */}

          </Routes>
        </AdminUsersProvider>
      </Router >
    </>);
}

export default App;